import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  downloadDeviceComplianceReport,
  exportActivityTraceReport,
  getFieldDeviceSerials,
  getCurrentBaselineRolloutSummary,
  getPreviousActiveRolloutSummary,
  getDeprecatedFirmwareSummary,
} from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { AccessDeniedDialog } from "../../components/ui/access-denied-dialog";
import { PieValueTooltip } from "../../components/ui/chart-tooltip";

const REPORT_TYPES = [
  {
    key: "ROLLOUT_SUMMARY",
    label: "Rollout Summary",
  },
  {
    key: "DEVICE_COMPLIANCE",
    label: "Device Compliance",
  },
  {
    key: "FULL_AUDIT_LOG",
    label: "Full Audit Log",
  },
];

const FORMATS = ["CSV", "PDF", "JSON"];

const ROLLOUT_SCOPE_FILTERS = [
  { key: "CURRENT_BASELINE", label: "Current Baseline" },
  { key: "PREVIOUS_ACTIVE", label: "Previous Active" },
  { key: "DEPRECATED", label: "Deprecated" },
];

const PIE_SLICES = [
  { key: "updated", label: "Updated", color: "#22c55e" },
  { key: "pending", label: "Pending", color: "#f59e0b" },
  { key: "mandatory_wait", label: "Mandatory Wait", color: "#f97316" },
  { key: "failed", label: "Failed", color: "#ef4444" },
];

const PREVIOUS_ACTIVE_PIE_SLICES = [
  { key: "received_it", label: "Received It", color: "#0d9488" },
  { key: "skipped", label: "Skipped", color: "#64748b" },
  { key: "failed", label: "Failed", color: "#ef4444" },
];

const DEPRECATED_PIE_SLICES = [
  { key: "active", label: "Active Devices", color: "#ef4444" },
  { key: "updated", label: "Updated", color: "#22c55e" },
  { key: "pending", label: "Pending", color: "#f59e0b" },
  { key: "mandatory_wait", label: "Mandatory Wait", color: "#f97316" },
  { key: "failed", label: "Failed", color: "#b91c1c" },
  { key: "received_it", label: "Received It", color: "#0d9488" },
  { key: "skipped", label: "Skipped", color: "#64748b" },
];

function getTodayDateString() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
}

// A CSV cell: nested values as JSON, quoted, and a leading = + - @ neutralised so a
// spreadsheet never runs it as a formula.
function csvCell(value) {
  let text = value === null || value === undefined ? "" : (typeof value === "object" ? JSON.stringify(value) : String(value));
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

// Rows of a rollout-summary response, whatever its scope: bucketed devices
// ({updated: [...], ...}), a device list, or deprecated builds each with devices.
function rolloutExportRows(scope, data) {
  if (!data || typeof data !== "object") return [];
  if (scope === "DEPRECATED") {
    return (data.deprecated_firmware || []).flatMap((fw) =>
      (fw.devices || []).map((d) => ({ firmware_version: fw.version, deprecated_at: fw.deprecated_at, ...d })));
  }
  const devices = data.devices;
  if (Array.isArray(devices)) return devices;
  if (devices && typeof devices === "object") {
    return Object.entries(devices).flatMap(([bucket, list]) =>
      (Array.isArray(list) ? list : []).map((d) => ({ bucket, ...d })));
  }
  return [];
}

function rowsToCsv(rows) {
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const lines = [columns.map(csvCell).join(",")];
  rows.forEach((r) => lines.push(columns.map((c) => csvCell(r[c])).join(",")));
  return lines.join("\r\n");
}

// A plain tabular PDF of audit-log entries (FMP-RP-003 PDF of the full-audit report).
function buildAuditLogPdf(activities, { fromDate, toDate, serialNumber }) {
  const pdf = new jsPDF("l", "mm", "a4");
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 14;
  pdf.setFontSize(13);
  pdf.text("Full Audit Log", 10, y);
  pdf.setFontSize(8);
  y += 6;
  pdf.text(`Range: ${fromDate || "-"} to ${toDate || "-"}   Device: ${serialNumber || "All Devices"}   Entries: ${activities.length}`, 10, y);
  y += 7;
  const header = ["Timestamp (UTC)", "Event", "Actor", "Role", "Details"];
  const x = [10, 58, 108, 158, 185];
  const drawHeader = () => { header.forEach((h, i) => pdf.text(h, x[i], y)); y += 5; };
  drawHeader();
  activities.forEach((a) => {
    if (y > pageHeight - 10) { pdf.addPage(); y = 14; drawHeader(); }
    const details = JSON.stringify(a.details || {});
    [a.timestamp, a.event_type, a.actor, a.actor_role, details.length > 70 ? `${details.slice(0, 67)}...` : details]
      .forEach((v, i) => pdf.text(String(v ?? ""), x[i], y));
    y += 5;
  });
  return pdf;
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatPublishedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

// Hoisted so the reason fragment is not a template literal nested inside
// another template literal (S4624).
function deprecatedFooterLeft(item) {
  const reason = item.deprecation_reason ? ` · ${item.deprecation_reason}` : "";
  return `Deprecated ${formatPublishedDate(item.deprecated_at)}${reason}`;
}

// Heading for the selected rollout scope. Hoisted out of the component: the
// chain was a nested ternary (S3358) whose last branch also nested a template
// literal inside another (S4624).
function scopeTitle(rolloutScope, currentBaselineLabel, previousActiveLabel, deprecatedRecord) {
  if (rolloutScope === "CURRENT_BASELINE") return currentBaselineLabel;
  if (rolloutScope === "PREVIOUS_ACTIVE") return previousActiveLabel;
  const version = deprecatedRecord?.version ? ` - ${deprecatedRecord.version}` : "";
  return `Deprecated${version}`;
}

// Legend-chip colours for the Previous Active breakdown. Hoisted out of the
// className template, where it was a ternary nested two deep (S3358).
function previousActiveChipClass(entryKey, selectedKey) {
  if (selectedKey !== entryKey) return "bg-white border-slate-300 text-slate-600";
  if (entryKey === "received_it") return "bg-teal-100 border-teal-300 text-teal-700";
  if (entryKey === "failed") return "bg-red-100 border-red-300 text-red-700";
  return "bg-slate-100 border-slate-400 text-slate-700";
}

function getDeprecatedStatusMeta() {
  return { label: "deprecated", className: "bg-red-100 text-red-700" };
}

function normalizeDeprecatedDevices(record) {
  if (Array.isArray(record?.devices)) {
    return record.devices.map((device, index) => ({
      ...device,
      device_id: device?.device_id || `${device?.serial_number || "device"}-${index}`,
      model: device?.model || device?.device_type || "",
      current_country: device?.current_country ?? device?.country ?? "",
      status: "deprecated",
      __rolloutCategory: "active",
    }));
  }

  if (record?.devices && !Array.isArray(record.devices)) {
    return Object.entries(record.devices).flatMap(([category, devices]) =>
      (devices ?? []).map((device, index) => ({
        ...device,
        device_id: device?.device_id || `${category}-${device?.serial_number || index}`,
        model: device?.model || device?.device_type || "",
        current_country: device?.current_country ?? device?.country ?? "",
        status: "deprecated",
        __rolloutCategory: category || "active",
      }))
    );
  }

  const deviceList = record?.devices_list ?? record?.device_list ?? record?.device_details ?? record?.active_devices_list;
  if (Array.isArray(deviceList)) {
    return deviceList.map((device, index) => ({
      ...device,
      device_id: device?.device_id || `${device?.serial_number || "device"}-${index}`,
      model: device?.model || device?.device_type || "",
      current_country: device?.current_country ?? device?.country ?? "",
      status: "deprecated",
      __rolloutCategory: "active",
    }));
  }

  return [];
}

function buildDeprecatedPieData(record) {
  const data = DEPRECATED_PIE_SLICES.map((slice) => ({
    ...slice,
    value: toNumber(record?.[slice.key], 0),
  })).filter((slice) => slice.value > 0);

  if (data.length > 0) return data;

  const fallbackActive = toNumber(record?.active_devices, 0);
  return fallbackActive > 0 ? [{ key: "active", label: "Active Devices", color: "#ef4444", value: fallbackActive }] : [];
}

// `rolloutCategory = ""` rather than `const category = rolloutCategory || ""`
// (S7760). Equivalent here: category is only ever compared with === against
// string literals, so a null argument behaves exactly as "" did.
function getStatusMeta(device, rolloutCategory = "") {
  const category = rolloutCategory;
  const rawStatus = String(device?.status || "").toLowerCase();

  if (category === "mandatory_wait") {
    return { label: "mandatory wait", className: "bg-orange-100 text-orange-700" };
  }
  if (category === "updated") {
    return { label: "updated", className: "bg-green-100 text-green-700" };
  }
  if (category === "failed") {
    return { label: "failed", className: "bg-red-100 text-red-700" };
  }
  if (category === "pending") {
    return { label: "pending", className: "bg-amber-100 text-amber-700" };
  }

  if (rawStatus === "updated") {
    return { label: "updated", className: "bg-green-100 text-green-700" };
  }
  if (rawStatus === "failed") {
    return { label: "failed", className: "bg-red-100 text-red-700" };
  }

  return { label: rawStatus || "pending", className: "bg-amber-100 text-amber-700" };
}

function getPreviousActiveStatusMeta(device, rolloutCategory) {
  const category = String(rolloutCategory || "").toLowerCase();
  const rawStatus = String(device?.status || device?.registration_status || "").toLowerCase();

  if (category === "received_it") {
    return { label: "received it", className: "bg-teal-100 text-teal-700" };
  }
  if (category === "skipped") {
    return { label: "skipped", className: "bg-slate-100 text-slate-700" };
  }
  if (category === "failed" || rawStatus === "failed") {
    return { label: "failed", className: "bg-red-100 text-red-700" };
  }
  if (rawStatus === "updated") {
    return { label: "updated", className: "bg-green-100 text-green-700" };
  }

  return { label: rawStatus || category || "unknown", className: "bg-slate-100 text-slate-700" };
}

function normalizePreviousActiveDevices(previousActive) {
  const categoryKeys = ["received_it", "skipped", "failed"];
  return categoryKeys.flatMap((category) =>
    (previousActive?.[`${category}_devices`] ?? []).map((device, index) => ({
      ...device,
      device_id: device?.device_id || `${category}-${device?.serial_number || index}`,
      model: device?.model || device?.device_type || "",
      current_country: device?.current_country ?? device?.country ?? "",
      status: device?.status || device?.registration_status || category,
      __rolloutCategory: category,
    }))
  );
}


// WP-RPT-05b filter gate for the Full Audit Log export: an incomplete or
// inverted date range blocks generation until corrected. Dates are YYYY-MM-DD
// strings from <input type="date">, so string comparison orders them correctly.
// The server applies the same rule (ExportReports returns 400), so this only
// spares the round trip - it is not the enforcement.
function getDateRangeError(fromDate, toDate) {
  // Name the field that is missing, not just that something is.
  if (!fromDate && !toDate) return "Select a From date and a To date.";
  if (!fromDate) return "Select a From date.";
  if (!toDate) return "Select a To date.";
  if (toDate < fromDate) return "The To date must be the same as or later than the From date.";
  return "";
}

export default function ExportPage() {
  // Read-only roles (Nurse/Patient) can view this page but downloads are refused (003 Role Gate).
  const { canAccess } = useAuth();
  const canDownload = canAccess("FleetAdmin", "ReleaseManager", "SupportEngineer");
  // FMP-AT-001: the fleet-wide audit log is read and exported by these roles only.
  const canExportAudit = canAccess("FleetAdmin", "ReleaseManager");
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const today = getTodayDateString();
  const [reportType, setReportType] = useState("ROLLOUT_SUMMARY");
  const [format, setFormat] = useState("CSV");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [serialFilter, setSerialFilter] = useState("");
  // Export filters for the compliance and rollout reports (FMP-RP-003). Empty by
  // default, so an unfiltered export is exactly what it was before.
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [exportSerial, setExportSerial] = useState("");
  const [exportCountry, setExportCountry] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  // Why the last Full Audit Log export was refused (e.g. no records in the
  // range). Previously the refusal only reached the console, so the operator
  // saw the button stop spinning and nothing else.
  const [exportError, setExportError] = useState("");
  const [serialOptions, setSerialOptions] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [currentBaseline, setCurrentBaseline] = useState(null);
  const [previousActive, setPreviousActive] = useState(null);
  const [deprecatedFirmware, setDeprecatedFirmware] = useState(null);
  const [rolloutLoading, setRolloutLoading] = useState(false);
  const [rolloutScope, setRolloutScope] = useState("CURRENT_BASELINE");
  const [selectedPreviousActiveSlice, setSelectedPreviousActiveSlice] = useState(null);
  const [selectedDeprecatedKey, setSelectedDeprecatedKey] = useState("");
  const [selectedDeprecatedSlice, setSelectedDeprecatedSlice] = useState(null);
  const rolloutSummaryRef = useRef(null);

  useEffect(() => {
    setSerialsLoading(true);
    getFieldDeviceSerials()
      .then((serials) => setSerialOptions(serials))
      .catch(() => setSerialOptions([]))
      .finally(() => setSerialsLoading(false));
  }, []);

  useEffect(() => {
    if (reportType !== "ROLLOUT_SUMMARY") return;
    let mounted = true;
    setRolloutLoading(true);
    // allSettled, not all: with Promise.all a single rejection reached one
    // catch that nulled all three scopes, so one slow endpoint emptied every
    // rollout option on this page even though the others had returned data.
    Promise.allSettled([
      getCurrentBaselineRolloutSummary(exportCountry, exportFromDate, exportToDate),
      getPreviousActiveRolloutSummary(),
      getDeprecatedFirmwareSummary(),
    ])
      .then(([current, previous, deprecated]) => {
        if (!mounted) return;
        const settle = (result, label) => {
          if (result.status === "fulfilled") return result.value ?? null;
          console.error(`[ExportPage] ${label} failed`, result.reason);
          return null;
        };
        setCurrentBaseline(settle(current, "current baseline"));
        setPreviousActive(settle(previous, "previous active"));
        setDeprecatedFirmware(settle(deprecated, "deprecated firmware"));
      })
      .finally(() => {
        if (mounted) setRolloutLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [reportType, exportCountry, exportFromDate, exportToDate]);

  // The rollout summary was PDF-only; it keeps PDF as its default format.
  useEffect(() => {
    setFormat(reportType === "ROLLOUT_SUMMARY" ? "PDF" : "CSV");
  }, [reportType]);

  const isFullAudit = reportType === "FULL_AUDIT_LOG";
  const isRolloutSummary = reportType === "ROLLOUT_SUMMARY";
  const showRightColumn = isFullAudit || isRolloutSummary;
  const dateRangeError = isFullAudit ? getDateRangeError(fromDate, toDate) : "";

  // A refusal stays on screen until the filter that caused it is corrected.
  useEffect(() => {
    setExportError("");
  }, [fromDate, toDate, serialFilter, reportType]);

  const rolloutSummaryData = useMemo(() => {
    const previousActiveVersion = previousActive?.previous_active || "--";
    const previousActiveTotal = toNumber(previousActive?.total, 0);
    const previousActiveReceived = toNumber(previousActive?.received_it, 0);
    const previousActiveSkipped = toNumber(previousActive?.skipped, 0);
    const previousActiveFailed = toNumber(previousActive?.failed, 0);
    const peakReceivedPercent = Math.max(0, Math.min(100, toNumber(previousActive?.peak_received_percent, 0)));
    const previousPublishedDate = formatPublishedDate(previousActive?.published_at);

    const previousActiveCard = {
      version: previousActiveVersion,
      label: "Previous Active",
      labelColor: "text-slate-400",
      borderColor: "border-slate-200",
      stats: [
        { value: String(previousActiveTotal), label: "TOTAL", color: "text-slate-900" },
        { value: String(previousActiveReceived), label: "RECEIVED IT", color: "text-teal-600" },
        { value: String(previousActiveSkipped), label: "SKIPPED", color: "text-slate-500" },
        { value: String(previousActiveFailed), label: "FAILED", color: "text-red-500" },
      ],
      progress: peakReceivedPercent,
      progressColor: "bg-teal-500",
      progressBg: "bg-slate-200",
      footer: {
        left: `${peakReceivedPercent.toFixed(1)}% of fleet received ${previousActiveVersion} at peak`,
        right: previousPublishedDate ? `Published ${previousPublishedDate}` : "",
      },
      key: previousActive?.firmware_id || previousActiveVersion,
    };

    const total = toNumber(currentBaseline?.total, 0);
    const updated = toNumber(currentBaseline?.updated, 0);
    const pending = toNumber(currentBaseline?.pending, 0);
    const mandatoryWait = toNumber(currentBaseline?.mandatory_wait, 0);
    const failed = toNumber(currentBaseline?.failed, 0);
    const completionPercent = Math.max(0, Math.min(100, toNumber(currentBaseline?.completion_percent, 0)));
    const publishedDate = formatPublishedDate(currentBaseline?.published_at);
    const version = currentBaseline?.current_baseline || "--";
    const currentBaselineCard = {
      version,
      label: "Current Baseline",
      labelColor: "text-slate-600",
      borderColor: "border-green-400",
      stats: [
        { value: String(total), label: "TOTAL", color: "text-slate-900" },
        { value: String(updated), label: "UPDATED", color: "text-green-600" },
        { value: String(pending), label: "PENDING", color: "text-amber-500" },
        { value: String(mandatoryWait), label: "MANDATORY WAIT", color: "text-red-500" },
        { value: String(failed), label: "FAILED", color: "text-red-600" },
      ],
      progress: completionPercent,
      progressColor: "bg-green-500",
      progressBg: "bg-slate-200",
      footer: {
        left: `${completionPercent.toFixed(2)}% complete`,
        right: publishedDate ? `Published ${publishedDate}` : "",
      },
      key: currentBaseline?.firmware_id || version,
    };

    const deprecatedCards = (deprecatedFirmware?.deprecated_firmware ?? []).map((item) => ({
      version: item.version || "--",
      label: "",
      labelColor: "",
      borderColor: "border-slate-200",
      stats: [
        { value: String(toNumber(item.active_devices, 0)), label: "ACTIVE DEVICES", color: "text-red-500" },
      ],
      progress: 0,
      progressColor: "bg-slate-300",
      progressBg: "bg-slate-100",
      footer: {
        left: deprecatedFooterLeft(item),
        right: "",
      },
      key: item.firmware_id || item.version,
    }));

    return {
      currentBaselineCard,
      previousActiveCard,
      deprecatedCards,
    };
  }, [currentBaseline, previousActive, deprecatedFirmware]);

  const pieData = useMemo(
    () =>
      PIE_SLICES.map((slice) => ({
        ...slice,
        value: toNumber(currentBaseline?.[slice.key], 0),
      })).filter((slice) => slice.value > 0),
    [currentBaseline]
  );

  const baselineDevices = currentBaseline?.devices ?? {};
  const tableDevices = Object.entries(baselineDevices).flatMap(([category, devices]) =>
    (devices ?? []).map((device) => ({
      ...device,
      __rolloutCategory: category,
    }))
  );
  const totalDevices = pieData.reduce((sum, item) => sum + item.value, 0);

  const previousActivePieData = useMemo(
    () =>
      PREVIOUS_ACTIVE_PIE_SLICES.map((slice) => ({
        ...slice,
        value: toNumber(previousActive?.[slice.key], 0),
      })).filter((slice) => slice.value > 0),
    [previousActive]
  );
  const previousActiveAllDevices = useMemo(() => normalizePreviousActiveDevices(previousActive), [previousActive]);
  const previousActiveTableDevices = useMemo(() => {
    if (!selectedPreviousActiveSlice) return previousActiveAllDevices;
    return previousActiveAllDevices.filter((device) => device.__rolloutCategory === selectedPreviousActiveSlice);
  }, [previousActiveAllDevices, selectedPreviousActiveSlice]);
  const previousActiveTotalDevices = previousActivePieData.reduce((sum, item) => sum + item.value, 0);

  const currentBaselineLabel = rolloutSummaryData?.currentBaselineCard?.label || "Current Baseline";
  const previousActiveLabel = rolloutSummaryData?.previousActiveCard?.label || "Previous Active";
  const deprecatedRecords = deprecatedFirmware?.deprecated_firmware ?? [];
  const selectedDeprecatedRecord = useMemo(() => {
    if (deprecatedRecords.length === 0) return null;
    return (
      deprecatedRecords.find((item) => (item.firmware_id || item.version) === selectedDeprecatedKey) ||
      deprecatedRecords[0]
    );
  }, [deprecatedRecords, selectedDeprecatedKey]);
  const deprecatedPieData = useMemo(() => buildDeprecatedPieData(selectedDeprecatedRecord), [selectedDeprecatedRecord]);
  const deprecatedAllDevices = useMemo(() => normalizeDeprecatedDevices(selectedDeprecatedRecord), [selectedDeprecatedRecord]);
  const deprecatedTableDevices = useMemo(() => {
    if (!selectedDeprecatedSlice) return deprecatedAllDevices;
    return deprecatedAllDevices.filter((device) => device.__rolloutCategory === selectedDeprecatedSlice);
  }, [deprecatedAllDevices, selectedDeprecatedSlice]);
  const deprecatedTotalDevices = deprecatedPieData.reduce((sum, item) => sum + item.value, 0);
  const selectedScopeTitle = scopeTitle(rolloutScope, currentBaselineLabel, previousActiveLabel, selectedDeprecatedRecord);

  useEffect(() => {
    if (deprecatedRecords.length === 0) {
      if (selectedDeprecatedKey) setSelectedDeprecatedKey("");
      return;
    }

    const hasSelectedRecord = deprecatedRecords.some((item) => (item.firmware_id || item.version) === selectedDeprecatedKey);
    if (!hasSelectedRecord) {
      setSelectedDeprecatedKey(deprecatedRecords[0].firmware_id || deprecatedRecords[0].version || "");
    }
  }, [deprecatedRecords, selectedDeprecatedKey]);

  useEffect(() => {
    setSelectedDeprecatedSlice(null);
  }, [selectedDeprecatedKey, rolloutScope]);

  useEffect(() => {
    setSelectedPreviousActiveSlice(null);
  }, [rolloutScope]);

  const downloadRolloutSummaryPdf = async () => {
    if (!rolloutSummaryRef.current) throw new Error("Rollout summary section is not ready yet.");

    const sourceNode = rolloutSummaryRef.current;
    const reportRoot = document.createElement("div");
    reportRoot.style.position = "fixed";
    reportRoot.style.left = "-100000px";
    reportRoot.style.top = "0";
    reportRoot.style.width = "900px";
    reportRoot.style.padding = "28px";
    reportRoot.style.background = "#f8fafc";
    reportRoot.style.boxSizing = "border-box";
    reportRoot.style.fontFamily = "inherit";

    const reportHeader = document.createElement("div");
    reportHeader.style.background = "#ffffff";
    reportHeader.style.border = "1px solid #e2e8f0";
    reportHeader.style.borderRadius = "14px";
    reportHeader.style.padding = "16px 18px";
    reportHeader.style.marginBottom = "16px";
    reportHeader.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
        <div>
          <h2 style="margin:0;color:#0f172a;font-size:24px;line-height:1.25;font-weight:800;">Rollout Summary - ${selectedScopeTitle}</h2>
        </div>
        <div style="text-align:right;color:#64748b;font-size:13px;line-height:1.4;">
          <div><strong style="color:#334155;">Generated</strong></div>
          <div>${today}</div>
        </div>
      </div>
    `;

    const reportContent = sourceNode.cloneNode(true);
    reportContent.style.margin = "0";
    reportContent.style.width = "100%";

    reportContent.querySelectorAll('[data-pdf-hide="true"]').forEach((node) => node.remove());
    reportContent.querySelectorAll("table").forEach((table) => {
      table.style.fontSize = "10px";
    });
    reportContent.querySelectorAll("th, td").forEach((cell) => {
      cell.style.paddingTop = "6px";
      cell.style.paddingBottom = "6px";
    });
    reportContent.querySelectorAll('[data-pdf-tight="true"]').forEach((node) => {
      node.style.marginTop = "0";
      node.style.paddingTop = "0";
    });

    reportRoot.appendChild(reportHeader);
    reportRoot.appendChild(reportContent);
    document.body.appendChild(reportRoot);

    let canvas;
    try {
      canvas = await html2canvas(reportRoot, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8fafc",
        windowWidth: reportRoot.scrollWidth,
        windowHeight: reportRoot.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });
    } finally {
      reportRoot.remove();
    }

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const topPadding = 18;
    const bottomPadding = 14;
    const contentWidth = pageWidth - margin * 2;
    const imageHeight = (canvas.height * contentWidth) / canvas.width;
    const usableHeight = pageHeight - topPadding - bottomPadding;
    const totalPages = Math.max(1, Math.ceil(imageHeight / usableHeight));

    const renderPageChrome = (pageIndex) => {
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.25);
      pdf.line(margin, 12, pageWidth - margin, 12);

      pdf.setFontSize(12);
      pdf.setTextColor(100, 116, 139);
      pdf.text("Rollout Summary Report", margin, 9);
      pdf.text(`${today}`, pageWidth - margin, 9, { align: "right" });

      pdf.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
      pdf.setFontSize(11);
      pdf.text(`Page ${pageIndex} of ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: "right" });
    };

    for (let page = 1; page <= totalPages; page += 1) {
      if (page > 1) pdf.addPage();
      renderPageChrome(page);
      const yPosition = topPadding - (page - 1) * usableHeight;
      pdf.addImage(imgData, "PNG", margin, yPosition, contentWidth, imageHeight, "", "FAST");
    }

    pdf.save(`rollout-summary-${rolloutScope.toLowerCase()}-${today}.pdf`);
  };

  const downloadBlob = (blob, filename, contentType = "application/octet-stream") => {
    const fileBlob = blob instanceof Blob ? blob : new Blob([blob], { type: contentType });
    const url = URL.createObjectURL(fileBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // One generator per report type. Each throws on failure; handleGenerate owns
  // the shared isGenerating bookkeeping and logs with the type-specific message.
  // Full audit log: the audited Activity Trace export (GET /device/fulllogs), which
  // replaces the unimplemented /Export-Report/FullAuditlog route (TP-FMP-05-DISC-001).
  const generateFullAuditLog = async () => {
    const selectedFormat = format.toLowerCase();
    const filters = { fromDate, toDate, serialNumber: serialFilter };
    const result = await exportActivityTraceReport({ format: selectedFormat, ...filters });
    if (selectedFormat === "pdf") {
      buildAuditLogPdf(result.activities, filters).save(`full-audit-log-${today}.pdf`);
      return;
    }
    downloadBlob(result.blob, result.filename);
  };

  const generateDeviceCompliance = async () => {
    const selectedFormat = ["pdf", "json"].includes(format.toLowerCase()) ? format.toLowerCase() : "csv";
    const { blob, filename, contentType } = await downloadDeviceComplianceReport(selectedFormat, {
      search: exportSerial,
      country: exportCountry,
      fromDate: exportFromDate,
      toDate: exportToDate,
    });
    downloadBlob(blob, filename || `device-compliance-report.${selectedFormat}`, contentType);
  };

  // Rollout summary: the scope's endpoint is called with ?export=<format> first, so the
  // export is audited server-side (REPORT_EXPORTED); CSV / JSON are built from that very
  // response, and the PDF is the on-screen summary as before (TP-FMP-05-DISC-016).
  const generateRolloutSummary = async () => {
    const selectedFormat = format.toLowerCase();
    let data;
    if (rolloutScope === "PREVIOUS_ACTIVE") {
      data = await getPreviousActiveRolloutSummary({ exportFormat: selectedFormat });
    } else if (rolloutScope === "DEPRECATED") {
      data = await getDeprecatedFirmwareSummary({ exportFormat: selectedFormat });
    } else {
      data = await getCurrentBaselineRolloutSummary(exportCountry, exportFromDate, exportToDate, selectedFormat);
    }
    const base = `rollout-summary-${rolloutScope.toLowerCase()}-${today}`;
    if (selectedFormat === "pdf") {
      await downloadRolloutSummaryPdf();
    } else if (selectedFormat === "json") {
      downloadBlob(JSON.stringify(data, null, 2), `${base}.json`, "application/json");
    } else {
      downloadBlob(rowsToCsv(rolloutExportRows(rolloutScope, data)), `${base}.csv`, "text/csv;charset=utf-8");
    }
  };

  const handleGenerate = async () => {
    if (isGenerating) return;

    if (!canDownload) {
      setAccessDeniedOpen(true);
      return;
    }

    if (reportType === "FULL_AUDIT_LOG" && !canExportAudit) {
      setAccessDeniedOpen(true);
      return;
    }

    if (reportType === "FULL_AUDIT_LOG" && getDateRangeError(fromDate, toDate)) {
      return;
    }

    const generators = {
      FULL_AUDIT_LOG: [generateFullAuditLog, "Full audit log export failed"],
      DEVICE_COMPLIANCE: [generateDeviceCompliance, "Device compliance download failed"],
      ROLLOUT_SUMMARY: [generateRolloutSummary, "Rollout summary export failed"],
    };
    const entry = generators[reportType];
    if (!entry) return;
    const [generate, errorMessage] = entry;

    try {
      setIsGenerating(true);
      setExportError("");
      await generate();
    } catch (err) {
      console.error(`[ExportPage] ${errorMessage}`, err);
      if (reportType === "FULL_AUDIT_LOG") setExportError(err?.message || errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Export Report</h1>
       
      </div>

      <div className={`grid grid-cols-1 gap-5 items-start ${showRightColumn ? "lg:grid-cols-2" : ""}`}>
        {/* Left column */}
        <div className="space-y-4">
          {/* Report Type */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-3">
              Report Type
            </p>
            <div className="space-y-2">
              {REPORT_TYPES.map((rt) => {
                const selected = reportType === rt.key;
                return (
                  <button
                    key={rt.key}
                    onClick={() => setReportType(rt.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                      selected
                        ? "border-teal-500 bg-teal-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    {/* Radio dot */}
                    <span
                      className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selected ? "border-teal-500" : "border-slate-300"
                      }`}
                    >
                      {selected && (
                        <span className="w-2 h-2 rounded-full bg-teal-500 block" />
                      )}
                    </span>
                    <div>
                      <p className={`text-sm font-semibold ${selected ? "text-teal-700" : "text-slate-800"}`}>
                        {rt.label}
                      </p>
                      <p className="text-xs text-slate-400">{rt.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Export Format */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-3">
              Export Format
            </p>

            {(
              <div className="flex items-center gap-2">
                {FORMATS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    className={`px-5 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                      format === f
                        ? "border-teal-500 bg-teal-50 text-teal-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}

            {/* Filters (FMP-RP-003): date range on the latest report, segment (country),
                and serial for compliance; date range and segment for the current baseline. */}
            {(reportType === "DEVICE_COMPLIANCE" || (isRolloutSummary && rolloutScope === "CURRENT_BASELINE")) && (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="export-filter-from" className="block text-xs text-slate-500 mb-1">From (optional)</label>
                  <input type="date" id="export-filter-from" value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                </div>
                <div>
                  <label htmlFor="export-filter-to" className="block text-xs text-slate-500 mb-1">To (optional)</label>
                  <input type="date" id="export-filter-to" value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                </div>
                <div>
                  <label htmlFor="export-filter-country" className="block text-xs text-slate-500 mb-1">Segment / country (optional)</label>
                  <input type="text" id="export-filter-country" defaultValue={exportCountry} placeholder="e.g. us"
                    onBlur={(e) => setExportCountry(e.target.value.trim())} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                </div>
                {reportType === "DEVICE_COMPLIANCE" && (
                  <div>
                    <label htmlFor="export-filter-serial" className="block text-xs text-slate-500 mb-1">Serial number (optional)</label>
                    <select id="export-filter-serial" value={exportSerial} disabled={serialsLoading}
                      onChange={(e) => setExportSerial(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200">
                      <option value="">{serialsLoading ? "Loading..." : "All Devices"}</option>
                      {serialOptions.map((sn) => (
                        <option key={sn} value={sn}>{sn}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {isRolloutSummary && (
              <div className="space-y-3 mt-4">
                <div>
                  <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-2">
                    Rollout View
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ROLLOUT_SCOPE_FILTERS.map((scope) => {
                      const isActive = rolloutScope === scope.key;
                      return (
                        <button
                          key={scope.key}
                          onClick={() => setRolloutScope(scope.key)}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                            isActive
                              ? "border-teal-500 bg-teal-50 text-teal-700"
                              : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                          }`}
                        >
                          {scope.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {!isFullAudit &&
              <button
                onClick={handleGenerate}
                disabled={isGenerating || (isRolloutSummary && rolloutLoading)}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#0d1fa8] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-3 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4" />
                {isGenerating ? "Generating..." : "Generate & Download Report"}
              </button>
            }
          </div>
        </div>

        {/* Right column */}
        {isFullAudit && <div className="space-y-4">
          {/* Date Range */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-3">
              Date Range
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="export-from-date" className="block text-xs text-slate-500 mb-1">From</label>
                <input
                  type="date"
                  id="export-from-date"
                  required
                  aria-invalid={isFullAudit && !fromDate}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200"
                />
              </div>
              <div>
                <label htmlFor="export-to-date" className="block text-xs text-slate-500 mb-1">To</label>
                <input
                  type="date"
                  id="export-to-date"
                  required
                  aria-invalid={isFullAudit && (!toDate || Boolean(fromDate && toDate < fromDate))}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200"
                />
              </div>
            </div>
            {dateRangeError && (
              <p role="alert" className="mt-2 text-xs text-red-600">{dateRangeError}</p>
            )}

            {/* Filter by Serial */}
            <div className="mt-3">
              <label htmlFor="export-serial-filter" className="block text-xs text-slate-500 mb-1">
                Filter by Serial Number
              </label>
              <select
                id="export-serial-filter"
                value={serialFilter}
                onChange={(e) => setSerialFilter(e.target.value)}
                disabled={serialsLoading}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white disabled:opacity-60"
              >
                <option value="">{serialsLoading ? "Loading..." : "All Devices"}</option>
                {serialOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || serialsLoading || Boolean(dateRangeError)}
                className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#0d1fa8] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-3 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4" />
                {isGenerating ? "Generating..." : "Generate & Download Report"}
              </button>
              {exportError && (
                <p role="alert" className="mt-2 text-xs text-red-600">{exportError}</p>
              )}
            </div>
          </div>
        </div>}

        {isRolloutSummary && (
          <RolloutSummaryPanel
            rolloutSummaryRef={rolloutSummaryRef}
            rolloutLoading={rolloutLoading}
            rolloutScope={rolloutScope}
            rolloutSummaryData={rolloutSummaryData}
            pieData={pieData}
            tableDevices={tableDevices}
            totalDevices={totalDevices}
            previousActivePieData={previousActivePieData}
            previousActiveTableDevices={previousActiveTableDevices}
            previousActiveTotalDevices={previousActiveTotalDevices}
            selectedPreviousActiveSlice={selectedPreviousActiveSlice}
            setSelectedPreviousActiveSlice={setSelectedPreviousActiveSlice}
            deprecatedPieData={deprecatedPieData}
            deprecatedTableDevices={deprecatedTableDevices}
            deprecatedTotalDevices={deprecatedTotalDevices}
            selectedDeprecatedRecord={selectedDeprecatedRecord}
            selectedDeprecatedSlice={selectedDeprecatedSlice}
            setSelectedDeprecatedSlice={setSelectedDeprecatedSlice}
            setSelectedDeprecatedKey={setSelectedDeprecatedKey}
          />
        )}
      </div>
      <AccessDeniedDialog
        open={accessDeniedOpen}
        onOpenChange={setAccessDeniedOpen}
        message="Your role does not have permission to download reports."
      />
    </div>
  );
}


/**
 * The rollout-summary report: baseline cards, pie charts and device tables for
 * the three scopes. Split out of ExportPage so its ~560 lines of conditional
 * rendering are scored on their own rather than on the page component (S3776).
 */
function RolloutSummaryPanel({
  rolloutSummaryRef,
  rolloutLoading,
  rolloutScope,
  rolloutSummaryData,
  pieData,
  tableDevices,
  totalDevices,
  previousActivePieData,
  previousActiveTableDevices,
  previousActiveTotalDevices,
  selectedPreviousActiveSlice,
  setSelectedPreviousActiveSlice,
  deprecatedPieData,
  deprecatedTableDevices,
  deprecatedTotalDevices,
  selectedDeprecatedRecord,
  selectedDeprecatedSlice,
  setSelectedDeprecatedSlice,
  setSelectedDeprecatedKey,
}) {
  return (
          <div className="space-y-4">
            <div ref={rolloutSummaryRef} className="space-y-4">
              {rolloutLoading ? (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <p className="text-sm text-slate-500">Loading rollout summary...</p>
                </div>
              ) : (
                <>
                  {rolloutScope === "CURRENT_BASELINE" && (
                    <CurrentBaselineScopePanel
                      rolloutSummaryData={rolloutSummaryData}
                      pieData={pieData}
                      tableDevices={tableDevices}
                      totalDevices={totalDevices}
                    />
                  )}

                  {rolloutScope === "PREVIOUS_ACTIVE" && (
                    <PreviousActiveScopePanel
                      rolloutSummaryData={rolloutSummaryData}
                      previousActivePieData={previousActivePieData}
                      previousActiveTableDevices={previousActiveTableDevices}
                      previousActiveTotalDevices={previousActiveTotalDevices}
                      selectedPreviousActiveSlice={selectedPreviousActiveSlice}
                      setSelectedPreviousActiveSlice={setSelectedPreviousActiveSlice}
                    />
                  )}

                  {rolloutScope === "DEPRECATED" && (
                    <DeprecatedScopePanel
                      rolloutSummaryData={rolloutSummaryData}
                      deprecatedPieData={deprecatedPieData}
                      deprecatedTableDevices={deprecatedTableDevices}
                      deprecatedTotalDevices={deprecatedTotalDevices}
                      selectedDeprecatedRecord={selectedDeprecatedRecord}
                      selectedDeprecatedSlice={selectedDeprecatedSlice}
                      setSelectedDeprecatedSlice={setSelectedDeprecatedSlice}
                      setSelectedDeprecatedKey={setSelectedDeprecatedKey}
                    />
                  )}
                </>
              )}
            </div>
          </div>
  );
}

/**
 * DEPRECATED view of the rollout summary. Split out of RolloutSummaryPanel so
 * each scope's chart/table branches are scored separately (S3776).
 */
function DeprecatedScopePanel({
  rolloutSummaryData,
  deprecatedPieData,
  deprecatedTableDevices,
  deprecatedTotalDevices,
  selectedDeprecatedRecord,
  selectedDeprecatedSlice,
  setSelectedDeprecatedSlice,
  setSelectedDeprecatedKey,
}) {
  return (
                    <div className="space-y-4">
                      {rolloutSummaryData.deprecatedCards.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-500">
                          No deprecated firmware records found.
                        </div>
                      ) : (
                        <>
                          <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-3">
                              Deprecated Firmware Version
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {rolloutSummaryData.deprecatedCards.map((card) => {
                                const cardKey = card.key || card.version;
                                const isActive = cardKey === (selectedDeprecatedRecord?.firmware_id || selectedDeprecatedRecord?.version);
                                return (
                                  <button
                                    key={cardKey}
                                    onClick={() => setSelectedDeprecatedKey(cardKey)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                                      isActive
                                        ? "border-red-300 bg-red-50 text-red-700"
                                        : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                                    }`}
                                  >
                                    {card.version}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {selectedDeprecatedRecord && (
                            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5 shadow-sm">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-lg font-bold text-slate-900">Deprecated Firmware Breakdown</h3>
                                  <p className="text-sm text-slate-500 mt-0.5">
                                    Version {selectedDeprecatedRecord.version || "--"}
                                  </p>
                                </div>
                                {selectedDeprecatedSlice && (
                                  <button
                                    onClick={() => setSelectedDeprecatedSlice(null)}
                                    data-pdf-hide="true"
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:text-slate-800"
                                  >
                                    Clear filter
                                  </button>
                                )}
                              </div>

                              <div className="grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active Devices</p>
                                  <p className="mt-1 text-2xl font-bold text-red-500">{toNumber(selectedDeprecatedRecord.active_devices, deprecatedTotalDevices)}</p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Deprecated On</p>
                                  <p className="mt-1 text-xl font-bold text-slate-900">{formatPublishedDate(selectedDeprecatedRecord.deprecated_at) || "—"}</p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reason</p>
                                  <p className="mt-1 text-xl font-bold text-slate-900">{selectedDeprecatedRecord.deprecation_reason || "—"}</p>
                                </div>
                              </div>

                              <div>
                                <h4 className="text-base font-semibold text-slate-800 mb-4">Device Breakdown</h4>
                                {deprecatedPieData.length === 0 ? (
                                  <p className="text-sm text-slate-400">No device data available.</p>
                                ) : (
                                  <div className="grid gap-6 md:grid-cols-[320px,1fr] items-center">
                                    <div className="h-[300px] rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
                                      <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                          <Pie
                                            data={deprecatedPieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={72}
                                            outerRadius={108}
                                            paddingAngle={2}
                                            dataKey="value"
                                            onClick={(entry) => setSelectedDeprecatedSlice((prev) => (prev === entry.key ? null : entry.key))}
                                            cursor="pointer"
                                          >
                                            {deprecatedPieData.map((entry) => (
                                              <Cell
                                                key={entry.key}
                                                fill={entry.color}
                                                opacity={selectedDeprecatedSlice && selectedDeprecatedSlice !== entry.key ? 0.35 : 1}
                                                stroke={selectedDeprecatedSlice === entry.key ? "#0f172a" : "#ffffff"}
                                                strokeWidth={selectedDeprecatedSlice === entry.key ? 3 : 2}
                                              />
                                            ))}
                                          </Pie>
                                          <Tooltip
                                            content={<PieValueTooltip />}
                                          />
                                          <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="28" fontWeight="700">
                                            {deprecatedTotalDevices}
                                          </text>
                                          <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="12" fontWeight="500">
                                            Total Devices
                                          </text>
                                        </PieChart>
                                      </ResponsiveContainer>
                                    </div>

                                    <div className="grid gap-2.5 sm:grid-cols-2">
                                      {deprecatedPieData.map((entry) => {
                                        const percent = deprecatedTotalDevices > 0 ? ((entry.value / deprecatedTotalDevices) * 100).toFixed(1) : "0.0";
                                        const isActive = selectedDeprecatedSlice === entry.key;
                                        return (
                                          <button
                                            key={entry.key}
                                            onClick={() => setSelectedDeprecatedSlice((prev) => (prev === entry.key ? null : entry.key))}
                                            className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                                              isActive
                                                ? "border-slate-400 bg-slate-50 shadow-sm"
                                                : "border-slate-200 bg-white hover:border-slate-300"
                                            }`}
                                          >
                                            <div className="flex items-center justify-between gap-3">
                                              <div className="flex items-center gap-2 min-w-0">
                                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                                <p className="text-xs font-semibold text-slate-700 truncate">{entry.label}</p>
                                              </div>
                                              <p className="text-[11px] text-slate-500 flex-shrink-0">{percent}%</p>
                                            </div>
                                            <p className="text-xl font-bold text-slate-900 mt-1">{entry.value}</p>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div data-pdf-hide="true" data-pdf-tight="true" className="flex flex-wrap gap-2 pt-1">
                                {deprecatedPieData.map((entry) => (
                                  <button
                                    key={entry.key}
                                    onClick={() => setSelectedDeprecatedSlice((prev) => (prev === entry.key ? null : entry.key))}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                                      selectedDeprecatedSlice === entry.key
                                        ? "bg-red-50 border-red-300 text-red-700"
                                        : "bg-white border-slate-300 text-slate-600"
                                    }`}
                                  >
                                    {entry.label} ({entry.value})
                                  </button>
                                ))}
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                  {selectedDeprecatedSlice
                                    ? `Filtered Devices (${deprecatedTableDevices.length})`
                                    : `All Devices (${deprecatedTableDevices.length})`}
                                </h4>
                                {deprecatedTableDevices.length === 0 ? (
                                  <p className="text-sm text-slate-400">No device-level details returned for this firmware.</p>
                                ) : (
                                  <div className="max-h-[420px] overflow-y-auto overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="min-w-full text-xs">
                                      <thead className="bg-slate-50 sticky top-0 z-10">
                                        <tr>
                                          {["Serial Number", "Model", "Current Country", "Current FW", "Base FW", "Status"].map((header) => (
                                            <th
                                              key={header}
                                              className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                                            >
                                              {header}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {deprecatedTableDevices.map((device) => {
                                          const statusMeta = getDeprecatedStatusMeta();
                                          return (
                                            <tr key={device.device_id || device.serial_number} className="hover:bg-slate-50 transition-colors">
                                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.serial_number ?? "—"}</td>
                                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.model ?? "—"}</td>
                                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_country ?? "—"}</td>
                                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_fw_version ?? device.current_fw ?? "—"}</td>
                                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.base_firmware_version ?? device.base_fw ?? "—"}</td>
                                              <td className="px-3 py-2 whitespace-nowrap">
                                                <span className={`inline-block px-2 py-0.5 rounded-full font-semibold capitalize ${statusMeta.className}`}>
                                                  {statusMeta.label}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
  );
}

/**
 * PREVIOUS_ACTIVE view of the rollout summary. Split out of RolloutSummaryPanel so
 * each scope's chart/table branches are scored separately (S3776).
 */
function PreviousActiveScopePanel({
  rolloutSummaryData,
  previousActivePieData,
  previousActiveTableDevices,
  previousActiveTotalDevices,
  selectedPreviousActiveSlice,
  setSelectedPreviousActiveSlice,
}) {
  return (
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">Previous Active Breakdown</h3>
                          <p className="text-sm text-slate-500 mt-0.5">
                            Version {rolloutSummaryData.previousActiveCard.version}
                          </p>
                        </div>
                        {selectedPreviousActiveSlice && (
                          <button
                            onClick={() => setSelectedPreviousActiveSlice(null)}
                            data-pdf-hide="true"
                            className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:text-slate-800"
                          >
                            Clear filter
                          </button>
                        )}
                      </div>

                      <div className="grid gap-3 md:grid-cols-4">
                        {rolloutSummaryData.previousActiveCard.stats.map((stat) => (
                          <div key={stat.label} className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
                            <p className={`mt-1 text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                          </div>
                        ))}
                      </div>

                      <div>
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div>
                            <h4 className="text-base font-semibold text-slate-800 mb-1">Device Breakdown</h4>
                            <p className="text-sm text-slate-500">{rolloutSummaryData.previousActiveCard.footer.left}</p>
                          </div>
                          {rolloutSummaryData.previousActiveCard.footer.right && (
                            <p className="text-sm text-slate-500">{rolloutSummaryData.previousActiveCard.footer.right}</p>
                          )}
                        </div>

                        {previousActivePieData.length === 0 ? (
                          <p className="text-sm text-slate-400">No device data available.</p>
                        ) : (
                          <div className="grid gap-6 lg:grid-cols-[360px,1fr] items-center">
                            <div className="h-[300px] rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={previousActivePieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={72}
                                    outerRadius={108}
                                    paddingAngle={2}
                                    dataKey="value"
                                    onClick={(entry) => {
                                      setSelectedPreviousActiveSlice((prev) => (prev === entry.key ? null : entry.key));
                                    }}
                                    cursor="pointer"
                                  >
                                    {previousActivePieData.map((entry) => (
                                      <Cell
                                        key={entry.key}
                                        fill={entry.color}
                                        opacity={selectedPreviousActiveSlice && selectedPreviousActiveSlice !== entry.key ? 0.35 : 1}
                                        stroke={selectedPreviousActiveSlice === entry.key ? "#0f172a" : "#ffffff"}
                                        strokeWidth={selectedPreviousActiveSlice === entry.key ? 3 : 2}
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip
                                    content={<PieValueTooltip />}
                                  />
                                  <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="28" fontWeight="700">
                                    {previousActiveTotalDevices}
                                  </text>
                                  <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="12" fontWeight="500">
                                    Total Devices
                                  </text>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>

                            <div className="grid gap-2.5 sm:grid-cols-2">
                              {previousActivePieData.map((entry) => {
                                const percent = previousActiveTotalDevices > 0 ? ((entry.value / previousActiveTotalDevices) * 100).toFixed(1) : "0.0";
                                const isActive = selectedPreviousActiveSlice === entry.key;
                                return (
                                  <button
                                    key={entry.key}
                                    onClick={() => setSelectedPreviousActiveSlice((prev) => (prev === entry.key ? null : entry.key))}
                                    className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                                      isActive
                                        ? "border-slate-400 bg-slate-50 shadow-sm"
                                        : "border-slate-200 bg-white hover:border-slate-300"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                        <p className="text-xs font-semibold text-slate-700 truncate">{entry.label}</p>
                                      </div>
                                      <p className="text-[11px] text-slate-500 flex-shrink-0">{percent}%</p>
                                    </div>
                                    <p className="text-lg font-bold text-slate-900 mt-1">{entry.value}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      <div data-pdf-hide="true" data-pdf-tight="true" className="flex flex-wrap gap-2 pt-1">
                        {previousActivePieData.map((entry) => (
                          <button
                            key={entry.key}
                            onClick={() => setSelectedPreviousActiveSlice((prev) => (prev === entry.key ? null : entry.key))}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${previousActiveChipClass(entry.key, selectedPreviousActiveSlice)}`}
                          >
                            {entry.label} ({entry.value})
                          </button>
                        ))}
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          {selectedPreviousActiveSlice
                            ? `${PREVIOUS_ACTIVE_PIE_SLICES.find((slice) => slice.key === selectedPreviousActiveSlice)?.label || "Filtered"} (${previousActiveTableDevices.length})`
                            : `All Devices (${previousActiveTableDevices.length})`}
                        </h4>
                        {previousActiveTableDevices.length === 0 ? (
                          <p className="text-sm text-slate-400">No devices in this category.</p>
                        ) : (
                          <div className="max-h-[420px] overflow-y-auto overflow-x-auto rounded-lg border border-slate-200">
                            <table className="min-w-full text-xs">
                              <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                  {["Serial Number", "Model", "Current Country", "Current FW", "Base FW", "Status"].map((header) => (
                                    <th
                                      key={header}
                                      className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {previousActiveTableDevices.map((device) => {
                                  const statusMeta = getPreviousActiveStatusMeta(device, device.__rolloutCategory);
                                  return (
                                    <tr key={device.device_id || device.serial_number} className="hover:bg-slate-50 transition-colors">
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.serial_number ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.model ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_country ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_fw_version ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.base_firmware_version ?? "—"}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={`inline-block px-2 py-0.5 rounded-full font-semibold capitalize ${statusMeta.className}`}>
                                          {statusMeta.label}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
  );
}

/**
 * CURRENT_BASELINE view of the rollout summary. Split out of RolloutSummaryPanel so
 * each scope's chart/table branches are scored separately (S3776).
 */
function CurrentBaselineScopePanel({
  rolloutSummaryData,
  pieData,
  tableDevices,
  totalDevices,
}) {
  return (
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">Current Baseline Breakdown</h3>
                          <p className="text-sm text-slate-500 mt-0.5">
                            Version {rolloutSummaryData.currentBaselineCard.version}
                          </p>
                        </div>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                        {rolloutSummaryData.currentBaselineCard.stats.map((stat) => (
                          <div key={stat.label} className="border border-slate-100 rounded-lg px-3 py-2 bg-slate-50/60">
                            <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mt-0.5">
                              {stat.label}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div className="grid gap-6 xl:grid-cols-[360px,1fr] items-center">
                        <div className="h-[300px] rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
                          {pieData.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-sm text-slate-400">
                              No device data available.
                            </div>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={72}
                                  outerRadius={108}
                                  paddingAngle={2}
                                  dataKey="value"
                                >
                                  {pieData.map((entry) => (
                                    <Cell
                                      key={entry.key}
                                      fill={entry.color}
                                      stroke="#ffffff"
                                      strokeWidth={2}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  content={<PieValueTooltip />}
                                />
                                <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="28" fontWeight="700">
                                  {totalDevices}
                                </text>
                                <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="12" fontWeight="500">
                                  Total Devices
                                </text>
                              </PieChart>
                            </ResponsiveContainer>
                          )}
                        </div>

                          <div className="grid gap-2.5 sm:grid-cols-1">
                          {pieData.map((entry) => {
                            const percent = totalDevices > 0 ? ((entry.value / totalDevices) * 100).toFixed(1) : "0.0";
                            return (
                              <div
                                key={entry.key}
                                className="text-left rounded-xl border px-3 py-2.5 border-slate-200 bg-white"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                    <p className="text-xs font-semibold text-slate-700 truncate">{entry.label}</p>
                                  </div>
                                  <p className="text-[11px] text-slate-500 flex-shrink-0">{percent}%</p>
                                </div>
                                <p className="text-lg font-bold text-slate-900 mt-1">{entry.value}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-700 mb-3">
                          All Devices ({tableDevices.length})
                        </h3>
                        {tableDevices.length === 0 ? (
                          <p className="text-sm text-slate-400">No devices in this category.</p>
                        ) : (
                          <div className="max-h-[420px] overflow-y-auto overflow-x-auto rounded-lg border border-slate-200">
                            <table className="min-w-full text-xs">
                              <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                  {["Serial Number", "Model", "Current FW", "Base FW", "Status"].map((header) => (
                                    <th
                                      key={header}
                                      className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {tableDevices.map((device) => {
                                  const statusMeta = getStatusMeta(device, device.__rolloutCategory);
                                  return (
                                    <tr key={device.device_id || device.serial_number} className="hover:bg-slate-50 transition-colors">
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.serial_number ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.model ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_fw_version ?? "—"}</td>
                                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.base_firmware_version ?? "—"}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={`inline-block px-2 py-0.5 rounded-full font-semibold capitalize ${statusMeta.className}`}>
                                          {statusMeta.label}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
  );
}